import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAlarms, useSiteNames, useSummary } from '../../data/hooks';
import { usePresenceCounters } from '../../realtime/aggregates';
import { Card, Kpi, PageHeader, SeverityBadge } from '../../ui/components';
import { canAny } from '../../api/client';
import { useMe } from '../auth/session';
import { formatNumber, formatRelative } from '../../ui/format';
import { EnergyChart } from './EnergyChart';
import { PresenceChart } from './PresenceChart';

export function DashboardPage() {
  const { t } = useTranslation();
  const summary = useSummary();
  const me = useMe().data;
  const live = usePresenceCounters();
  // The authority observer sees no alarms (no alarm.view): no count, no list, no request.
  const alarmView = canAny(me, 'alarm.view');
  const alarms = useAlarms(alarmView);
  const siteNames = useSiteNames();
  const s = summary.data;
  return (
    <div className="page">
      <PageHeader title={t('dashboard.title')} />
      <div className="grid grid-kpi">
        <Kpi label={t('dashboard.devices')} value={s ? formatNumber(s.devices) : '…'} />
        <Kpi label={t('dashboard.online')} value={formatNumber(live.online)} tone="online" />
        <Kpi label={t('dashboard.offline')} value={formatNumber(live.offline)} tone="offline" />
        {alarmView && <Kpi label={t('dashboard.alarms')} value={formatNumber(live.alarms)} tone="critical" />}
        {/* Gateways are shared infrastructure: only shown to roles that can see them. */}
        {canAny(me, 'gateway.view') && (
          <Kpi label={t('dashboard.gateways')} value={s ? `${s.gatewaysOnline}/${s.gatewaysTotal}` : '…'} />
        )}
      </div>
      <PresenceChart />
      <EnergyChart />
      <div className={alarmView ? 'grid grid-2' : 'grid'}>
        <Card title={t('dashboard.byType')}>
          <dl className="kv">
            {Object.entries(s?.byType ?? {}).map(([type, n]) => [
              <dt key={`${type}-t`}>
                {t(`catalog.deviceType.${type}` as 'catalog.deviceType.other', { defaultValue: type })}
              </dt>,
              <dd key={`${type}-n`} className="num" style={{ textAlign: 'left' }}>
                {formatNumber(n)}
              </dd>,
            ])}
          </dl>
        </Card>
        {alarmView && (
          <Card title={t('dashboard.recentAlarms')} bodyClass="">
            <table className="table">
              <tbody>
                {(alarms.data ?? []).slice(0, 8).map((a) => (
                  <tr key={a.id}>
                    <td>
                      <SeverityBadge severity={a.severity} />
                    </td>
                    <td>
                      <Link to={`/devices/${a.deviceId}`}>{a.deviceName}</Link>
                    </td>
                    <td className="muted hide-mobile">{siteNames.get(a.siteId)}</td>
                    <td className="muted">{formatRelative(a.openedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
